<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from '@/composables/useI18n'
import { useStatus } from '@/composables/useStatus'
import { useFilters } from '@/composables/useFilters'
import { useEvents } from '@/composables/useEvents'
import { useFloatingUI } from '@/composables/useFloatingUI'

const { t, lang, getSeverityLabel } = useI18n()
const { overallSeverity, statusKey, statusClass, affectedServicesAndComponents } = useStatus()
const { toggleChip } = useFilters()
const { servicesList, findServiceByName, findComponentByName, getServiceLabel, getComponentLabel } = useEvents()

const isOpen = ref(false)
const buttonRef = ref<HTMLElement | null>(null)
const { floatingEl, floatingStyles } = useFloatingUI(buttonRef)

function toggle() {
  isOpen.value = !isOpen.value
}

function close() {
  isOpen.value = false
}

function handleServiceClick(name: string, label: string) {
  toggleChip('service', name, label)
  close()
}

function handleComponentClick(name: string, label: string) {
  toggleChip('component', name, label)
  close()
}

function onClickOutside(e: Event) {
  const el = (e.target as HTMLElement).closest('.overall-status')
  if (!el) close()
}

onMounted(() => document.addEventListener('click', onClickOutside))
onUnmounted(() => document.removeEventListener('click', onClickOutside))
</script>

<template>
  <div class="overall-status">
    <button
      ref="buttonRef"
      type="button"
      class="status-button"
      :aria-expanded="isOpen"
      :aria-label="t('system-status-detail')"
      @click.stop="toggle"
    >
      <span class="status-indicator" :class="statusClass"></span>
      <span class="status-text">{{ t(statusKey) }}</span>
    </button>
    <div v-if="isOpen" ref="floatingEl" :style="floatingStyles" class="status-dropdown" role="region" :aria-label="t('system-status-detail')">
      <div class="status-dropdown-content">
        <h3 class="status-dropdown-title">{{ t('system-status-detail') }}</h3>
        <div class="status-dropdown-items">
          <div class="status-dropdown-subtitle">
            {{ affectedServicesAndComponents.length === 0 ? t('all-systems-operational') : t('affected-services') }}
          </div>

          <!-- All operational -->
          <template v-if="affectedServicesAndComponents.length === 0">
            <div
              v-for="service in servicesList"
              :key="service.name"
              class="status-dropdown-item status-item-operational"
              style="cursor: pointer"
              @click="handleServiceClick(service.name, getServiceLabel(service, lang))"
            >
              <span class="status-item-indicator" aria-hidden="true"></span>
              <span class="status-item-label">{{ getServiceLabel(service, lang) }}</span>
            </div>
          </template>

          <!-- Affected services + components -->
          <template v-else>
            <template v-for="item in affectedServicesAndComponents" :key="`${item.type}:${item.name}`">
              <div
                v-if="item.type === 'service'"
                :class="['status-dropdown-item', `status-item-${item.severity}`]"
                style="cursor: pointer"
                @click="handleServiceClick(item.name, getServiceLabel(findServiceByName(item.name), lang))"
              >
                <span class="status-item-indicator" aria-hidden="true"></span>
                <span class="status-item-label">{{ getServiceLabel(findServiceByName(item.name), lang) }}</span>
                <span class="status-item-severity">{{ getSeverityLabel(item.severity) }}</span>
              </div>
              <div
                v-else
                :class="['status-dropdown-item', `status-item-${item.severity}`, 'status-item-component']"
                style="cursor: pointer"
                @click="handleComponentClick(item.name, getComponentLabel(findComponentByName(item.name), lang))"
              >
                <span class="status-item-indicator" aria-hidden="true"></span>
                <span class="status-item-label">{{ getComponentLabel(findComponentByName(item.name), lang) }}</span>
                <span class="status-item-severity">{{ getSeverityLabel(item.severity) }}</span>
              </div>
            </template>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
